import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  InputLabel,
} from '@mui/material';

import BackButton from '../../../common/BackButton';
import ConfirmModal from '../../../common/modals/ConfirmModal';

// NETWORK
import useAdminCountryRequest from '../../../../requests/AdminCountryRequest';

// BREADCRUMB
import { useBreadcrumb } from './BreadcrumbContext';

const ServiceCountryPrefixMapSingleView = ({
  prefixMap,
  country,
  isNew,
  errorHandler,
  navigate,
}) => {
  const params = useParams();
  const { addBreadcrumb, dropBreadcrumb } = useBreadcrumb();

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const { createNewPrefixMap, updatePrefixMap, deletePrefixMapById } =
    useAdminCountryRequest();

  useEffect(() => {
    addBreadcrumb(1, `Prefix Map: ${prefixMap.before_prefix}`);
    return () => {
      dropBreadcrumb(1);
    };
  }, [prefixMap]);

  useEffect(() => {
    if (params.childId === '0') {
      setIsEditing(true);
    }
  }, [params]);

  /*
    Handles Edit Mode on/off
  */
  const toggleEdit = () => {
    if (params.childId === '0') {
      navigate(-1);
      return;
    }

    setIsEditing(!isEditing);
  };

  /*
    Handles changes in the form
  */
  const handleFormChange = (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      if (prefixMap.country_prefix_map_id) {
        await updatePrefixMap(
          country.country_code_id,
          prefixMap.country_prefix_map_id,
          edits,
          errorHandler
        );
      } else {
        await createNewPrefixMap(country.country_code_id, edits, errorHandler);
      }

      navigate(-1);
    } catch (e) {
      console.log('Have err:', e);
      // Handle error
    }
  };

  const confirmDelete = () => {
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    setShowConfirmDelete(false);
    await deletePrefixMapById(
      country.country_code_id,
      prefixMap.country_prefix_map_id,
      errorHandler
    );
    navigate(-1);
  };

  /*
    Generates the Lander heading and text section
  */
  const generateLander = () => {
    return (
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        sx={{ paddingBottom: 2 }}
      >
        <BackButton onClick={() => navigate(-1)} />
        <Typography variant="h4">Country Prefix Map Information</Typography>
      </Stack>
    );
  };

  const generateBody = () => {
    if (isNew) {
      return (
        <form onSubmit={handleSubmit} onChange={handleFormChange}>
          <Grid container spacing={2} maxWidth={800}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Before Prefix
                </InputLabel>
                <TextField
                  name="before_prefix"
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  After Prefix
                </InputLabel>
                <TextField
                  name="after_prefix"
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Length
                </InputLabel>
                <TextField
                  name="tn_length"
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Box display="flex" gap={2}>
                <Button variant="text" color="secondary" onClick={toggleEdit}>
                  Cancel
                </Button>
                <Button variant="contained" color="secondary" type="submit">
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      );
    }

    if (!prefixMap) {
      return <Typography>No Data</Typography>;
    }

    if (isEditing) {
      return (
        <form onSubmit={handleSubmit} onChange={handleFormChange}>
          <Grid container spacing={2} maxWidth={800}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Before Prefix
                </InputLabel>
                <TextField
                  name="before_prefix"
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                  defaultValue={prefixMap.before_prefix}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  After Prefix
                </InputLabel>
                <TextField
                  name="after_prefix"
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                  defaultValue={prefixMap.after_prefix}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Length
                </InputLabel>
                <TextField
                  name="tn_length"
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                  defaultValue={prefixMap.tn_length}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Box display="flex" gap={2}>
                <Button variant="text" color="secondary" onClick={toggleEdit}>
                  Cancel
                </Button>
                <Button variant="contained" color="secondary" type="submit">
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography>
            <strong>Before Prefix:</strong> {prefixMap.before_prefix}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography>
            <strong>After Prefix:</strong> {prefixMap.after_prefix}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography>
            <strong>Length:</strong> {prefixMap.tn_length}
          </Typography>
        </Grid>

        <Grid size={12}>
          <Box display="flex" gap={2}>
            <Button variant="contained" color="secondary" onClick={toggleEdit}>
              Edit
            </Button>
            <Button color="error" onClick={confirmDelete} variant="contained">
              Delete
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {generateLander()}
      {generateBody()}
      <ConfirmModal
        show={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this prefix map?"
      />
    </>
  );
};

export default ServiceCountryPrefixMapSingleView;
